<template>
  <fade-transition :duration="150">
    <div
      v-if="isModalVisible"
      class="modal-wrapper__overlay hide-scrollbar"
      :class="contentWrapperDynamicClass"
    >
      <div
        v-on-clickaway="onClickAway"
        :class="CONTENT_WRAPPER_CLASS_NAME"
        :style="contentWrapperStyle"
        @mousedown="onMouseDown"
        @mouseup="onMouseUp"
      >
        <a-scrollable-content>
          <slot :close-modal="closeModal" :props="props" />
        </a-scrollable-content>
        <div v-if="closeButton" class="modal-wrapper__close-wrapper">
          <div @click.stop="closeModal" class="close-icon" />
        </div>
      </div>
    </div>
  </fade-transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { directive as onClickaway } from 'vue-clickaway'

import { propValidator, PROP_TYPES } from '@/utils/validators'
import AScrollableContent from 'shared/AScrollableContent'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'

export const TRANSITIONS = {
  FADE: 'fade',
  SLIDE_UP: 'slide-up'
}

export const MODAL = {}

const TRANSITION_VALUES = Object.values(TRANSITIONS)

const CONTENT_WRAPPER_CLASS_NAME = 'modal-wrapper__content-wrapper'

export default {
  name: 'AModalWrapper',
  mixins: [hydrationHelpers],
  components: { AScrollableContent },
  directives: { onClickaway },
  props: {
    modalName: propValidator([PROP_TYPES.STRING]),
    width: propValidator([PROP_TYPES.NUMBER], false),
    closeButton: propValidator([PROP_TYPES.BOOLEAN], false, false),
    closeOnClickAway: propValidator([PROP_TYPES.BOOLEAN], false, true),
    showScrollOnClose: propValidator([PROP_TYPES.BOOLEAN], false, true),
    transition: propValidator(
      [PROP_TYPES.STRING],
      false,
      TRANSITIONS.FADE,
      value => TRANSITION_VALUES.includes(value)
    ),
    transparent: propValidator([PROP_TYPES.BOOLEAN], false, false),
    parentClassesToPreventClickaway: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => []
    ),
    parentIdsToPreventClickaway: propValidator(
      [PROP_TYPES.ARRAY],
      false,
      () => []
    )
  },
  data() {
    return {
      CONTENT_WRAPPER_CLASS_NAME,
      isModalVisible: false,
      isClickedInsideModalWindow: false,
      props: {}
    }
  },
  computed: {
    ...mapGetters({
      isAppleBrowser: 'isAppleBrowser',
      mobileBottomPadding: 'mobileBottomPadding',
      isModalVisibleByName: 'modal/isModalVisibleByName',
      isPageNavigationInProgress: 'isPageNavigationInProgress'
    }),
    contentWrapperStyle() {
      if (!this.$_hydrationHelpers_isLayoutMobile) {
        return {
          width: `${this.width}px`
        }
      }

      return { paddingBottom: `${this.mobileBottomPadding}px` }
    },
    contentWrapperDynamicClass() {
      return {
        transparent: this.transparent
      }
    }
  },
  watch: {
    isPageNavigationInProgress(newVal) {
      if (!newVal) return

      this.closeModal()
    }
  },
  methods: {
    ...mapActions({
      setModalToOpenState: 'modal/setModalToOpenState',
      setModalToCloseState: 'modal/setModalToCloseState'
    }),
    onClickAway(e) {
      const target = e.target
      const isTargetInsideContentWrapper = this.$helper.hasParentWith(target, {
        className: CONTENT_WRAPPER_CLASS_NAME
      })
      const isClickawayShouldBePrevented = [
        ...this.parentClassesToPreventClickaway.map(parentClass =>
          this.$helper.hasParentWith(target, { className: parentClass })
        ),
        ...this.parentIdsToPreventClickaway.map(parentId =>
          this.$helper.hasParentWith(target, { id: parentId })
        )
      ].some(r => r)

      if (
        isTargetInsideContentWrapper ||
        isClickawayShouldBePrevented ||
        this.$helper.isElementCaptchaOverlay(e.target)
      )
        return

      if (this.closeOnClickAway) {
        this.closeModal()
      }
    },
    showScrollBar() {
      this.$_hydrationHelpers_showScroll()
      this.$helper.getTopStickyElement().classList.remove('nofit')
    },
    showScrollHandler() {
      if (this.$_hydrationHelpers_isLayoutMobile) {
        this.showScrollBar()
      } else {
        /**
         * this timeout is used to wait when fade transition is complete.
         * In other case two scroll appears for some moment and move content left.
         * Transition delay is set assets/scss/utils/_animations.scss
         */
        setTimeout(() => {
          this.showScrollBar()
        }, 200)
      }
    },
    closeModal() {
      if (this.isClickedInsideModalWindow) {
        this.isClickedInsideModalWindow = false
        return
      }

      if (this.showScrollOnClose) this.showScrollHandler()

      if (!this.isModalVisible) return

      this.isModalVisible = false
      this.setModalToCloseState(this.modalName)
      this.$emit('close-modal', this.props)
    },
    onMouseDown() {
      this.isClickedInsideModalWindow = true
    },
    onMouseUp() {
      this.isClickedInsideModalWindow = false
    }
  },
  mounted() {
    this.$bus.$on(`open-modal-${this.modalName}`, (props = {}) => {
      if (this.isModalVisibleByName(this.modalName)) {
        console.log(
          `The modal with the name ${this.modalName} is already opened.`
        )
        return
      }

      this.props = props
      this.isModalVisible = true
      this.setModalToOpenState(this.modalName)
      this.$_hydrationHelpers_hideScroll()
      this.$helper.getTopStickyElement().classList.add('nofit')
      this.$emit('open-modal', props)
    })

    this.$bus.$on(`close-modal-${this.modalName}`, () => this.closeModal())
  },
  beforeDestroy() {
    if (this.isModalVisible) {
      this.showScrollBar()
    }
    this.$bus.$off(`open-modal-${this.modalName}`)
  }
}
</script>

<style lang="scss" scoped>
.modal-wrapper__overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  overflow-y: scroll;
  background: rgba(0, 0, 0, 0.5);
  z-index: $z-index-modal-wrapper;

  .modal-wrapper__content-wrapper {
    position: relative;
    display: flex;
    max-height: 100%;
    margin: auto;
    background: #fff;
    overflow: hidden;

    @include on-laptop-max {
      position: relative;
    }

    @include mobile {
      height: 100%;
      width: 100%;
    }
  }

  &.transparent .modal-wrapper__content-wrapper {
    background: transparent;
  }

  .modal-wrapper__close-wrapper {
    position: absolute;
    width: 100%;
    height: 35px;
    top: 0;
    left: 0;
    background: #fff;
    z-index: 10;

    .close-icon {
      right: 10px;
      top: 10px;
    }
  }
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: top 500ms;
}

.slide-up-enter,
.slide-up-leave-to {
  top: 100vh;
}
</style>
